import type {
  TelemetryData,
  VehicleStatusResponseSuccess,
} from "@strtmn/sds/types"
import type { HasPkSk } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import React, { type PropsWithChildren } from "react"
import { FaSignal } from "react-icons/fa6"
import * as W from "./Widgets"
import type { WidgetWithTitleProps } from "./Widgets"

export interface VehicleDashboardProps {
  vehicleStatus: VehicleStatusResponseSuccess
}

interface TelemetryItem extends HasPkSk {
  Value: any
}

export function findTelemetryItem(
  data: TelemetryData,
  keys: string[]
): TelemetryItem | undefined {
  let item = undefined
  for (const key of keys) {
    if (data[key]) {
      item = data[key] as HasPkSk & { Value: any }
      break
    }
  }
  return item
}

interface TelemetryNumberWidgetProps extends WidgetWithTitleProps {
  defaultUnit: string
  factor?: number | undefined
  precision?: number | undefined
  data: TelemetryData
  keys: string[]
}

export function TelemetryNumberWidget({
  title,
  IconNode,
  defaultUnit,
  factor,
  precision,
  indicatorTheme,
  data,
  keys,
}: TelemetryNumberWidgetProps) {
  const item = findTelemetryItem(data, keys)
  if (!item) {
    return (
      <W.TextWidget
        title={title}
        IconNode={IconNode}
        value={I18n.get("Keine Daten")}
      />
    )
  }
  return (
    <W.NumberWidget
      title={title}
      IconNode={IconNode}
      indicatorTheme={indicatorTheme}
      factor={factor}
      precision={precision}
      value={item.Value}
      unit={item["unit"] ?? defaultUnit}
    />
  )
}

interface TelemetryPercentageWidgetProps extends WidgetWithTitleProps {
  precision?: number | undefined
  data: TelemetryData
  keys: string[]
}

export function TelemetryPercentageWidget({
  title,
  IconNode,
  precision,
  indicatorTheme,
  data,
  keys,
}: TelemetryPercentageWidgetProps) {
  const item = findTelemetryItem(data, keys)
  if (!item) {
    return (
      <W.TextWidget
        title={title}
        IconNode={IconNode}
        value={I18n.get("Keine Daten")}
      />
    )
  }
  return (
    <W.PercentageWidget
      title={title}
      IconNode={IconNode}
      indicatorTheme={indicatorTheme}
      precision={precision}
      value={item.Value}
    />
  )
}

/**
 * Presence telemetry isn't a plain {Value: ...} datapoint: the connection
 * kind is encoded in the key itself ("Presence-aja" = Fernwartung,
 * "Presence" = Data), and the status lives in `eventType`.
 * See StatusDetails.tsx for the same convention.
 */
export function findPresenceItem(
  data: TelemetryData,
  kind: "data" | "remote-management"
): HasPkSk | undefined {
  const key = Object.keys(data).find((k) =>
    kind === "remote-management"
      ? k.startsWith("Presence") && k.endsWith("-aja")
      : k.startsWith("Presence") && !k.endsWith("-aja")
  )
  return key ? (data[key] as HasPkSk) : undefined
}

interface SignalBandKeys {
  band2G: string
  band3G: string
  band4G: string
}

const SIGNAL_STRENGTH_KEYS: SignalBandKeys = {
  band2G: `${0x0000}@${0x0035}/ModemCon2G`,
  band3G: `${0x0000}@${0x0035}/ModemCon3G`,
  band4G: `${0x0000}@${0x0035}/ModemConLTE`,
}

const SIGNAL_STRENGTH_BANDS: Array<[string, string]> = [
  ["2G", SIGNAL_STRENGTH_KEYS.band2G],
  ["3G", SIGNAL_STRENGTH_KEYS.band3G],
  ["4G/LTE", SIGNAL_STRENGTH_KEYS.band4G],
]

interface ConnectivityWidgetProps {
  data: TelemetryData
}

/**
 * Reflects the data connection only (Fernwartung/remote-management is
 * ignored). Machines on older software don't report per-band signal
 * strength yet, so this falls back to a plain connected/disconnected text
 * until a signal-strength band value shows up, at which point a connected
 * link renders as a percentage bar for the currently active band instead.
 */
export function ConnectivityWidget({ data }: ConnectivityWidgetProps) {
  const title = "Datenverbindung"
  const IconNode = <FaSignal />
  const eventType = findPresenceItem(data, "data")?.["eventType"] as
    | string
    | undefined
  const connected = eventType === "connected"

  const bandValues = SIGNAL_STRENGTH_BANDS.map(([band, key]) => ({
    band,
    value: findTelemetryItem(data, [key])?.Value as number | undefined,
  }))
  const hasSignalStrengthData = bandValues.some(
    ({ value }) => value !== undefined
  )

  if (!hasSignalStrengthData) {
    if (eventType === undefined) {
      return (
        <W.TextWidget
          title={title}
          IconNode={IconNode}
          value={I18n.get("Keine Daten")}
          indicatorTheme="error"
        />
      )
    }
    return (
      <W.TextWidget
        title={title}
        IconNode={IconNode}
        value={connected ? I18n.get("Verbunden") : I18n.get("Getrennt")}
        indicatorTheme={connected ? "ok" : "error"}
      />
    )
  }

  if (!connected) {
    return (
      <W.TextWidget
        title={title}
        IconNode={IconNode}
        value={I18n.get("Getrennt")}
        indicatorTheme="error"
      />
    )
  }

  const active = bandValues.find(
    (b): b is { band: string; value: number } =>
      b.value !== undefined && b.value !== -1
  )
  if (!active) {
    // Connected per presence, but momentarily no band reports a value.
    return (
      <W.TextWidget
        title={title}
        IconNode={IconNode}
        value={I18n.get("Verbunden")}
        indicatorTheme="ok"
      />
    )
  }

  return (
    <W.PercentageWidget
      title={`${title} (${active.band})`}
      IconNode={IconNode}
      precision={0}
      value={active.value}
      indicatorTheme={active.value === 0 ? "warn" : "ok"}
    />
  )
}

// ---- Dashboard layout components ----

interface DashboardGroupProps extends PropsWithChildren {
  title: string
  IconNode?: React.ReactNode
}

export function DashboardGroup({
  title,
  IconNode,
  children,
}: DashboardGroupProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="dashboard-group-head">
        {IconNode && <div className="gh-icon">{IconNode}</div>}
        <span className="gh-title">{title}</span>
      </div>
      {children}
    </div>
  )
}

export function DashboardColumns({ children }: PropsWithChildren) {
  return <div className="dashboard-columns">{children}</div>
}

export function DashboardColumn({ children }: PropsWithChildren) {
  return <div className="dashboard-col">{children}</div>
}
